/**
 * Analytics & Reports Page
 *
 * Comprehensive analytics dashboard with three views:
 * - Trends: Time-series charts of score progression
 * - Comparison: Tables comparing departments/operators/SOPs
 * - Operator Deep Dive: Individual operator analytics
 *
 * Features URL state persistence for shareable filter configurations.
 */

import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  Users,
  Download,
  FileText,
  Filter,
  Calendar,
  Building2,
  X,
} from 'lucide-react';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import {
  useAnalyticsTrends,
  useAnalyticsComparison,
  useExportAnalytics,
  type TrendParams,
  type ComparisonParams,
} from '../../hooks/useAnalytics';
import { TrendChart } from './components/TrendChart';
import { ComparisonTable } from './components/ComparisonTable';
import { OperatorAnalytics } from './components/OperatorAnalytics';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

// Tab definitions
type TabId = 'trends' | 'comparison' | 'operator';

interface Tab {
  id: TabId;
  label: string;
  icon: typeof TrendingUp;
}

const TABS: Tab[] = [
  { id: 'trends', label: 'Trends', icon: TrendingUp },
  { id: 'comparison', label: 'Comparison', icon: BarChart3 },
  { id: 'operator', label: 'Operator Deep Dive', icon: Users },
];

// Period options (0 = All Time, no date filter)
const PERIOD_OPTIONS = [
  { value: 0, label: 'All Time' },
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last year' },
];

// Group by options (for trends)
const GROUP_BY_OPTIONS = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

// Compare by options
const COMPARE_BY_OPTIONS = [
  { value: 'department', label: 'Departments' },
  { value: 'operator', label: 'Operators' },
  { value: 'sop', label: 'SOPs' },
];

export function AnalyticsPage() {
  useDocumentTitle('Analytics');
  // URL state management
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL params with defaults
  const activeTab = (searchParams.get('tab') as TabId) || 'trends';
  const period = parseInt(searchParams.get('period') || '0', 10);
  const groupBy = (searchParams.get('groupBy') as 'day' | 'week' | 'month') || 'day';
  const departmentId = searchParams.get('departmentId') || '';
  const compareBy = (searchParams.get('compareBy') as 'department' | 'operator' | 'sop') || 'department';
  const operatorId = searchParams.get('operatorId') || null;

  // Sort state (not in URL for simplicity)
  const [sortBy, setSortBy] = useState('overall');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch departments for filter dropdown
  const { data: departments } = useDepartmentLookup();

  // Build query params
  const trendParams: TrendParams = useMemo(
    () => ({
      period,
      groupBy,
      departmentId: departmentId || undefined,
    }),
    [period, groupBy, departmentId]
  );

  const comparisonParams: ComparisonParams = useMemo(
    () => ({
      compareBy,
      period,
      departmentId: departmentId || undefined,
      limit: 20,
      sortBy,
      sortOrder,
    }),
    [compareBy, period, departmentId, sortBy, sortOrder]
  );

  // Fetch data based on active tab
  const {
    data: trendData,
    isLoading: trendsLoading,
  } = useAnalyticsTrends(trendParams);

  const {
    data: comparisonData,
    isLoading: comparisonLoading,
  } = useAnalyticsComparison(comparisonParams);

  const exportMutation = useExportAnalytics();

  // Update URL params helper
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '') {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        });
        return next;
      });
    },
    [setSearchParams]
  );

  // Tab change handler
  const handleTabChange = useCallback(
    (tab: TabId) => {
      updateParams({ tab });
    },
    [updateParams]
  );

  // Filter handlers
  const handlePeriodChange = useCallback(
    (value: string) => {
      updateParams({ period: value });
    },
    [updateParams]
  );

  const handleGroupByChange = useCallback(
    (value: string) => {
      updateParams({ groupBy: value });
    },
    [updateParams]
  );

  const handleDepartmentChange = useCallback(
    (value: string) => {
      updateParams({ departmentId: value || null });
    },
    [updateParams]
  );

  const handleCompareByChange = useCallback(
    (value: string) => {
      updateParams({ compareBy: value });
    },
    [updateParams]
  );

  const handleOperatorChange = useCallback(
    (id: string | null) => {
      updateParams({ operatorId: id });
    },
    [updateParams]
  );

  const handleSort = useCallback((column: string) => {
    setSortBy((prevBy) => {
      if (prevBy === column) {
        setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
        return column;
      }
      setSortOrder('desc');
      return column;
    });
  }, []);

  // Clear filters
  const handleClearFilters = useCallback(() => {
    updateParams({
      period: '30',
      groupBy: 'day',
      departmentId: null,
      compareBy: 'department',
      operatorId: null,
    });
    setSortBy('overall');
    setSortOrder('desc');
  }, [updateParams]);

  const hasActiveFilters =
    period !== 30 ||
    groupBy !== 'day' ||
    departmentId !== '' ||
    (activeTab === 'comparison' && compareBy !== 'department') ||
    (activeTab === 'operator' && operatorId !== null);

  // Export handlers
  const handleExportCsv = useCallback(() => {
    if (activeTab === 'trends') {
      exportMutation.mutate({
        format: 'csv',
        type: 'trends',
        period,
        groupBy,
        departmentId: departmentId || undefined,
      });
    } else if (activeTab === 'comparison') {
      exportMutation.mutate({
        format: 'csv',
        type: 'comparison',
        period,
        compareBy,
        departmentId: departmentId || undefined,
      });
    } else if (activeTab === 'operator' && operatorId) {
      exportMutation.mutate({
        format: 'csv',
        type: 'operator',
        period,
        operatorId,
      });
    }
  }, [activeTab, exportMutation, period, groupBy, departmentId, compareBy, operatorId]);

  const handleExportPdf = useCallback(() => {
    if (activeTab === 'trends') {
      exportMutation.mutate({
        format: 'pdf',
        type: 'trends',
        period,
        groupBy,
        departmentId: departmentId || undefined,
      });
    } else if (activeTab === 'comparison') {
      exportMutation.mutate({
        format: 'pdf',
        type: 'comparison',
        period,
        compareBy,
        departmentId: departmentId || undefined,
      });
    } else if (activeTab === 'operator' && operatorId) {
      exportMutation.mutate({
        format: 'pdf',
        type: 'operator',
        period,
        operatorId,
      });
    }
  }, [activeTab, exportMutation, period, groupBy, departmentId, compareBy, operatorId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/20">
              <BarChart3 size={20} className="text-white" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Analytics & Reports
            </h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Track performance trends, compare metrics, and generate detailed reports
          </p>
        </div>

        {/* Export Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCsv}
            disabled={exportMutation.isPending}
            className="
              inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white
              px-3 py-2 text-sm font-medium text-gray-700
              shadow-sm transition-all duration-200
              hover:bg-gray-50 hover:shadow-md
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            <Download size={16} />
            CSV
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exportMutation.isPending}
            className="
              inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white
              px-3 py-2 text-sm font-medium text-gray-700
              shadow-sm transition-all duration-200
              hover:bg-gray-50 hover:shadow-md
              disabled:cursor-not-allowed disabled:opacity-50
            "
          >
            <FileText size={16} />
            PDF
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Filter size={16} />
              <span className="hidden sm:inline">Filters:</span>
            </div>

            {/* Period Selector */}
            <div className="relative">
              <Calendar
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <select
                value={period}
                onChange={(e) => handlePeriodChange(e.target.value)}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Group By (only for Trends tab) */}
            {activeTab === 'trends' && (
              <select
                value={groupBy}
                onChange={(e) => handleGroupByChange(e.target.value)}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 px-3 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                {GROUP_BY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            {/* Compare By (only for Comparison tab) */}
            {activeTab === 'comparison' && (
              <select
                value={compareBy}
                onChange={(e) => handleCompareByChange(e.target.value)}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 px-3 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                {COMPARE_BY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            {/* Department Filter */}
            <div className="relative">
              <Building2
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <select
                value={departmentId}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                className="
                  appearance-none rounded-lg border border-gray-200 bg-white
                  py-2 pl-9 pr-8
                  text-sm text-gray-700
                  transition-colors duration-200
                  hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                "
              >
                <option value="">All Departments</option>
                {departments?.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="
                  flex items-center gap-1.5 rounded-lg
                  px-3 py-2
                  text-sm font-medium text-gray-500
                  transition-colors duration-200
                  hover:bg-gray-100 hover:text-gray-700
                "
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>

          {/* Summary Stats (for Trends) */}
          {activeTab === 'trends' && trendData?.summary && (
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">
                Avg Score:{' '}
                <span className="font-semibold text-gray-900">
                  {trendData.summary.averageOverall !== null
                    ? trendData.summary.averageOverall.toFixed(1)
                    : '—'}
                </span>
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-500">
                Total:{' '}
                <span className="font-semibold text-gray-900">
                  {trendData.summary.totalAssessments} assessments
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  group flex items-center gap-2 border-b-2 pb-3 pt-1 text-sm font-medium transition-colors
                  ${
                    isActive
                      ? 'border-guild-green text-guild-green'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
              >
                <Icon
                  size={18}
                  className={isActive ? 'text-guild-green' : 'text-gray-400 group-hover:text-gray-500'}
                />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        {/* Trends Tab */}
        {activeTab === 'trends' && (
          <div className="p-6">
            <TrendChart data={trendData?.data ?? []} isLoading={trendsLoading} />
          </div>
        )}

        {/* Comparison Tab */}
        {activeTab === 'comparison' && (
          <ComparisonTable
            data={comparisonData?.data ?? []}
            compareBy={compareBy}
            isLoading={comparisonLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        )}

        {/* Operator Deep Dive Tab */}
        {activeTab === 'operator' && (
          <div className="p-6">
            <OperatorAnalytics
              operatorId={operatorId}
              period={period}
              onSelectOperator={handleOperatorChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AnalyticsPage;
